// src/pages/Upgrades.jsx
import { useEffect, useState, useRef } from "react";
import Sidebar from "../components/Sidebar";
import { useSidebarCollapsed } from "../utils/useSidebarCollapsed";
import { ShieldAlert, X, UserSearch, Target, Send, Sparkles, Cpu, Network, Fingerprint, Share2, Mail, Newspaper, Zap, Key, Building, Globe, Database, MessageSquare, Terminal, ShieldCheck, Lock, Check, Gavel, Scale, ClipboardList } from "lucide-react";
import { redirectToLogin } from "../utils/auth";
import CheckoutModal from "../components/CheckoutModa"; // fixed typo
import { refreshEntitlements } from "../utils/brandProtection";

const CONTACT_API = "https://whiteintel.io/api/regular/app/v2/contact/contact.php";
const PAYMENT_LINKS_API = "https://whiteintel.io/api/regular/app/v2/user/get_payment_links.php";

const frosted =
  "border border-sky-900/30 bg-[#0a1224]/70 backdrop-blur-md shadow-[0_0_10px_rgba(56,189,248,0.05)]";

export default function Upgrades() {
  // layout
  const [collapsed, setCollapsed] = useSidebarCollapsed();
  const [mobileOpen, setMobileOpen] = useState(false);

  // billing cycle state
  const [billingCycle, setBillingCycle] = useState("monthly"); // "monthly" | "yearly"

  // checkout modal
  const [checkoutUrl, setCheckoutUrl] = useState(null);

  // contact modal
  const [contactOpen, setContactOpen] = useState(false);
  const [forPlan, setForPlan] = useState(null); 

  // payment links fetched from backend
  const [planLinks, setPlanLinks] = useState(null);
  const [linksError, setLinksError] = useState(null);

  // tabs
  const [activeTab, setActiveTab] = useState("licenses"); // "licenses" | "addons"

  // URL <-> tab sync
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get("tab");
    if (t === "licenses" || t === "addons") setActiveTab(t);
  }, []);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (activeTab === "addons") params.set("tab", "addons");
    else params.delete("tab");
    const qs = params.toString();
    window.history.replaceState(
      {},
      "",
      window.location.pathname + (qs ? `?${qs}` : "") + window.location.hash
    );
  }, [activeTab]);

  const openModal = (plan) => {
    setForPlan(plan);
    setContactOpen(true);
  };

  function getJwtToken() {
    try {
      return (
        localStorage.getItem("jwtToken") ||
        localStorage.getItem("token") ||
        sessionStorage.getItem("jwtToken") ||
        window.WHITEINTEL_TOKEN ||
        ""
      );
    } catch {
      return "";
    }
  }

  // fetch payment links
  useEffect(() => {
    let ignore = false;
    async function loadLinks() {
      setLinksError(null);
      try {
        const res = await fetch(PAYMENT_LINKS_API, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getJwtToken()}`,
          },
        });

        if ([401, 419, 440].includes(res.status)) {
          redirectToLogin();
          return;
        }

        const json = await res.json().catch(() => ({}));

        if (!ignore && json?.success && json?.allowed && json?.links) {
          setPlanLinks(json.links);
        } else {
          setPlanLinks(null); 
        }
      } catch (e) {
        if (!ignore) {
          setPlanLinks(null);
          setLinksError("Unable to load payment links right now.");
        }
      }
    }
    loadLinks();
    return () => {
      ignore = true;
    };
  }, []);

  const threatIntelLinks = planLinks?.threat_intel;
  const threatFeedAddonUrl = threatIntelLinks?.addons?.threat_feed_api_yearly || null;

  return (
    <div className="min-h-screen bg-[#000414] text-white">
      {/* Header spacer */}
      <header
        className="bg-[#0a1224]/85 border-b border-sky-800/40 backdrop-blur sticky top-0 z-40 shadow-[0_6px_16px_-8px_rgba(0,0,0,0.7)]"
        style={{ marginLeft: collapsed ? "56px" : "224px" }}
      >
        <div className="px-6 h-14 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <h1 className="text-[20px] font-bold leading-tight tracking-tight">Upgrades & Licensing</h1>
          </div>
        </div>
      </header>

      {/* Sub-nav — outside the header */}
      <div
        className="border-b border-white/10 bg-[#000414]"
        style={{ marginLeft: collapsed ? "56px" : "224px" }}
      >
        <div className="px-6 flex items-center gap-8">
          <button
            onClick={() => setActiveTab("licenses")}
            className={`py-2 text-sm font-medium transition whitespace-nowrap -mb-px border-b-2 ${
              activeTab === "licenses"
                ? "text-white border-sky-500"
                : "text-neutral-400 hover:text-white border-transparent"
            }`}
          >
            Licenses
          </button>
          <button
            onClick={() => setActiveTab("addons")}
            className={`py-2 text-sm font-medium transition whitespace-nowrap -mb-px border-b-2 ${
              activeTab === "addons"
                ? "text-white border-sky-500"
                : "text-neutral-400 hover:text-white border-transparent"
            }`}
          >
            Add-ons
          </button>
        </div>
      </div>

      <div className="flex">
        <Sidebar
          collapsed={collapsed}
          setCollapsed={setCollapsed}
          mobileOpen={mobileOpen}
          setMobileOpen={setMobileOpen}
          activeKey="upgrades"
          onChange={() => {}}
        />

        <main className="flex-1 px-4 md:px-6 pb-12">
          
          {/* Billing Cycle Toggle (Only show on Licenses tab) */}
          {activeTab === "licenses" && (
             <div className="flex justify-center mb-8 mt-4">
              <div className="bg-[#0a1224] p-1 rounded-md border border-sky-900/30 inline-flex relative">
                <button 
                  onClick={() => setBillingCycle("monthly")}
                  className={`px-6 py-2 rounded-md text-xs font-bold uppercase tracking-widest transition-all ${
                    billingCycle === "monthly" 
                    ? "bg-sky-600 text-white shadow-sm" 
                    : "text-neutral-400 hover:text-white"
                  }`}
                >
                  Monthly
                </button>
                <button 
                  onClick={() => setBillingCycle("yearly")}
                  className={`relative px-6 py-2 rounded-md text-xs font-bold uppercase tracking-widest transition-all ${
                    billingCycle === "yearly" 
                    ? "bg-sky-600 text-white shadow-sm" 
                    : "text-neutral-400 hover:text-white"
                  }`}
                >
                  Yearly
                  {/* Badge */}
                  <span className={`absolute -top-5 -right-8 px-2.5 py-1 rounded-full text-[10px] font-bold tracking-tight shadow-sm transition-all whitespace-nowrap ${
                    billingCycle === "yearly"
                    ? "bg-sky-400 text-white scale-110 shadow-[0_0_10px_rgba(56,189,248,0.5)]"
                    : "bg-sky-900/50 text-sky-200 border border-sky-500/30"
                  }`}>
                   Save ~%20
                  </span>
                </button>
              </div>
            </div>
          )}

          {/* Licenses */}
          {activeTab === "licenses" && (
            <div className="max-w-7xl grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Researcher Plan */}
              <PlanCard
                name="Researcher"
                icon={Zap}
                priceMonthly="$200"
                priceYearly="$2000 / year"
                billingCycle={billingCycle}
                features={[
                  { text: "Fully unmasked data access", enabled: true },
                  { text: "Search by consumer, corporate, email, and attack surface identifiers", enabled: true },
                  { text: "750 data unlocks per month", enabled: true },
                  { text: "10 data exports per month (2-month look-back window)", enabled: true },
                  { text: "10 watchlist tokens for domain monitoring", enabled: true },
                  { text: "5 watchlist tokens for email monitoring", enabled: true },
                  { text: "Real-time alerts (meta-data only)", enabled: true },
                  { text: "1 user seat", enabled: true },
                  { text: "Standard support", enabled: true },
                  { text: "API access", enabled: false },
                  { text: "Investigation+ Module", enabled: false },
                  { text: "Full stealer log archive downloads (when available)", enabled: false },
                  { text: "Direct content access for watchlist events", enabled: false },
                  { text: "Threat Feed access", enabled: false },
                  { text: "Webhook integration", enabled: false },
                  { text: "SSO, Jira and Slack integrations", enabled: false },
                  { text: "Dark Web mention monitoring", enabled: false },
                  { text: "Public GitHub repository scanning", enabled: false },
                  { text: "Look-alike domain detection", enabled: false },
                  { text: "Computer name and IP address identifiers", enabled: false },
                  { text: "Resale or customer-facing product rights", enabled: false },
                ]}
                links={planLinks?.researcher}
                onOpenCheckout={(url) => setCheckoutUrl(url)}
                onContact={() => openModal("Researcher")}
              />

              {/* Enterprise Plan */}
              <PlanCard
                name="Enterprise"
                icon={ShieldCheck}
                priceMonthly="$400"
                priceYearly="$4000 / year"
                billingCycle={billingCycle}
                features={[
                  { text: "Fully unmasked data access", enabled: true },
                  { text: "Search by all core identifiers (consumer, corporate, email, attack surface)", enabled: true },
                  { text: "Unlimited data unlocks", enabled: true },
                  { text: "10 data exports per day (1-year look-back window)", enabled: true },
                  { text: "64 watchlist tokens for domains and emails with real-time alerts", enabled: true },
                  { text: "Dark Web mention monitoring for 8 keywords", enabled: true },
                  { text: "Look-alike domain monitoring", enabled: true },
                  { text: "Public GitHub repository scanning for 8 repositories", enabled: true },
                  { text: "50 API calls per day", enabled: true },
                  { text: "Direct content access for watchlist events", enabled: true },
                  { text: "Threat Feed access", enabled: true },
                  { text: "SSO, Jira and Slack integrations", enabled: true },
                  { text: "Real-time alerts (CSV included)", enabled: true },
                  { text: "2 user seats", enabled: true },
                  { text: "Priority support", enabled: true },
                  { text: "Webhook integration", enabled: false },
                  { text: "Computer name and IP address identifiers", enabled: false },
                  { text: "Investigation+ Module", enabled: false },
                  { text: "Full stealer log archive downloads (when available)", enabled: false },
                  { text: "Threat Feed API", enabled: false },
                  { text: "Resale or customer-facing product rights", enabled: false },
                ]}
                availableAddons={["Threat Feed API", "Investigation+", "Managed Takedowns"]}
                links={planLinks?.enterprise}
                onOpenCheckout={(url) => setCheckoutUrl(url)}
                onContact={() => openModal("Enterprise")}
              />

              {/* Threat Intelligence Plan */}
              <PlanCard
                name="Threat Intelligence"
                icon={Lock}
                priceMonthly="$750"
                priceYearly="$7500 / year"
                billingCycle={billingCycle}
                highlight
                features={[
                  { text: "Fully unmasked data access", enabled: true },
                  { text: "All search identifiers included (domains, emails, IPs, computer names, etc.)", enabled: true },
                  { text: "Unlimited data unlocks", enabled: true },
                  { text: "500 data exports per day (2-year look-back window)", enabled: true },
                  { text: "128 watchlist tokens for all identifiers with real-time alerts", enabled: true },
                  { text: "Dark Web mention monitoring for 16 keywords", enabled: true },
                  { text: "Look-alike domain monitoring", enabled: true },
                  { text: "Public GitHub repository scanning for 16 repositories", enabled: true },
                  { text: "Investigation+ Module", enabled: true },
                  { text: "Full stealer log archive downloads (when available)", enabled: true },
                  { text: "500 API calls per day", enabled: true },
                  { text: "Direct content access for watchlist events", enabled: true },
                  { text: "Threat Feed access", enabled: true },
                  { text: "SSO, Jira and Slack integrations", enabled: true },
                  { text: "Real-time alerts (CSV included)", enabled: true },
                  { text: "Webhook integration and data streaming", enabled: true },
                  { text: "Resale and customer-facing product rights", enabled: true },
                  { text: "3 user seats", enabled: true },
                  { text: "Priority developer support", enabled: true },
                  { text: "Threat Feed API", enabled: false },
                ]}
                availableAddons={["Threat Feed API", "Managed Takedowns"]}
                links={threatIntelLinks}
                onOpenCheckout={(url) => setCheckoutUrl(url)}
                onContact={() => openModal("Threat Intelligence")}
              />
            </div>
          )}

          {/* Add-ons */}
          {activeTab === "addons" && (
            <div className="max-w-7xl grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              <AddonCard
                name="Threat Feed API"
                description="A comprehensive stream of structured dark-web telemetry providing real-time visibility into darkweb chatters, data & access sales, ransomware lifecycle events, and emerging adversary methodologies."
                quota="50 API calls/day"
                priceNumber={3000}
                priceUnit="USD / Year"
                note="Only available for yearly purchases."
                checkoutUrl={threatFeedAddonUrl}
                onBuy={() => setCheckoutUrl(threatFeedAddonUrl)}
                onContact={() => openModal("Threat Feed API")}
                contactEmail="info@whiteintel.io"
              />

              <AddonCardInvestigations
                onContact={() => openModal("Investigation+ Module")}
              />

              <AddonCardTakedowns
                onContact={() => openModal("Managed Takedowns")}
              />
            </div>
          )}

          {/* Optional: error banner if links failed to load */}
          {linksError && (
            <div className="max-w-7xl mt-6">
              <div className="rounded-md border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-amber-100">
                {linksError}
              </div>
            </div>
          )}
        </main>
      </div>

      {/* Checkout modal (single instance) */}
      <CheckoutModal
        open={!!checkoutUrl}
        checkoutUrl={checkoutUrl}
        onClose={() => {
          setCheckoutUrl(null);
          // The user may have just upgraded — re-check entitlements so the
          // sidebar locks/pills update without waiting for them to refresh.
          refreshEntitlements();
        }}
        title="WhiteIntel — Secure Checkout"
      />

      {/* Contact modal */}
      <ContactLicenseModal
        open={contactOpen}
        planName={forPlan}
        onClose={() => setContactOpen(false)}
      />
    </div>
  );
}

/* ---------------- UI Bits ---------------- */

function TabButton({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      className={[
        "px-3 py-2 text-sm rounded-md border transition",
        active
          ? "border-sky-500/40 bg-sky-500/10 text-sky-200"
          : "border-sky-900/30 bg-[#0a1224]/70 text-neutral-300 hover:bg-white/5",
      ].join(" ")}
    >
      {children}
    </button>
  );
}

/* ---------------- Components ---------------- */

function PlanCard({
  name,
  icon: Icon,
  billingCycle,
  priceMonthly,
  priceYearly,
  features = [],
  availableAddons = [],
  onContact,
  links,
  onOpenCheckout,
  highlight = false,
}) {
  const hasLinks = !!(links && (links.monthly || links.yearly));

  const activePrice = billingCycle === "monthly" ? priceMonthly : priceYearly.split(' /')[0];
  const activePeriod = billingCycle === "monthly" ? "/ month" : "/ year";

  // Determine correct link based on cycle
  const activeLink = billingCycle === "monthly" ? links?.monthly : links?.yearly;

  return (
    <div
      className={`relative flex flex-col p-8 rounded-md transition-all duration-300 ${
        highlight 
          ? "bg-gradient-to-b from-[#0a1224] to-[#000414] border-2 border-sky-500/50 shadow-[0_0_30px_rgba(14,165,233,0.15)]" 
          : "bg-[#0a1224]/80 border border-white/10 hover:border-white/20"
      }`}
    >
      {highlight && (
        <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-sky-500 text-white text-[10px] font-bold uppercase tracking-widest px-4 py-1.5 rounded-full shadow-lg">
          Most Capable
        </div>
      )}

      <div className="flex items-center justify-between mb-4">
        <div className={`p-2 rounded-md ${highlight ? 'bg-sky-500/20 text-sky-400' : 'bg-white/5 text-neutral-400'}`}>
          <Icon className="h-5 w-5" />
        </div>
        <h3 className="text-lg font-bold uppercase tracking-tighter text-white">{name}</h3>
      </div>

      <div className="mb-6 border-b border-white/5 pb-6">
        <div className="flex items-baseline gap-1">
          <span className="text-4xl font-mono font-bold tracking-tight text-white">
            {activePrice}
          </span>
          <span className="text-xs font-medium text-neutral-500 uppercase tracking-tighter">
            {activePeriod}
          </span>
        </div>
        
        <div className="mt-2 space-y-1">
          {billingCycle === "yearly" ? (
            <p className="text-[11px] font-bold text-sky-400 uppercase tracking-wide">
              Billed annually
            </p>
          ) : (
            <p className="text-[11px] font-bold text-neutral-500 uppercase tracking-wide">
              Cancel anytime
            </p>
          )}
          <p className="text-[10px] font-medium text-neutral-600 uppercase tracking-tight">
            + Applicable Taxes
          </p>
        </div>
      </div>

      <div className="flex-1 space-y-1 mb-6">
        {features.map((f, i) => {
           const text = typeof f === "string" ? f : f.text;
           const enabled = typeof f === "string" ? true : f.enabled;
           return (
             <div key={i} className="flex items-start py-1">
               {enabled ? (
                 <Check className="h-4 w-4 mt-1 text-sky-500 shrink-0" />
               ) : (
                 <X className="h-4 w-4 mt-1 text-neutral-700 shrink-0" />
               )}
               <span className={`ml-3 text-[13px] leading-tight ${enabled ? "text-neutral-300 font-medium" : "text-neutral-600 line-through decoration-neutral-700"}`}>
                 {text}
               </span>
             </div>
           );
        })}
      </div>

      {availableAddons.length > 0 && (
        <div className="pt-4 mb-6 border-t border-white/5">
          <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-bold mb-2">
            Available Add-ons
          </div>
          <div className="flex flex-wrap gap-1.5">
            {availableAddons.map((a) => (
              <span
                key={a}
                className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-sky-500/10 border border-sky-500/30 text-sky-200"
              >
                <Sparkles className="h-3 w-3" />
                {a}
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="pt-6 border-t border-white/5 mt-auto">
        {hasLinks && activeLink ? (
           <button
             onClick={() => onOpenCheckout?.(activeLink)}
             className={`w-full py-4 rounded-md font-bold text-sm transition-all ${
               highlight 
                ? "bg-sky-500 hover:bg-sky-400 text-white shadow-[0_0_20px_rgba(14,165,233,0.2)]" 
                : "bg-white text-black hover:bg-neutral-200"
             }`}
           >
             Subscribe {billingCycle === "yearly" ? "Yearly" : "Monthly"}
           </button>
        ) : (
          <button
            onClick={onContact}
            className="w-full py-4 rounded-md bg-white/5 border border-white/10 text-neutral-300 hover:bg-white/10 font-bold text-sm transition-all flex items-center justify-center gap-2"
          >
            <Send className="w-4 h-4" />
            Contact Sales Team
          </button>
        )}
      </div>
    </div>
  );
}


function AddonCard({
  name,
  description,
  priceNumber,   
  priceUnit,     
  note,
  checkoutUrl,   
  onBuy,         
  onContact,     
  quota,         
  contactEmail,  
}) {
  return (
    <div className={`rounded-md ${frosted} p-5 flex flex-col justify-between transition-all hover:border-sky-500/40 group bg-[#000414]`}>
      <div className="space-y-4">
        {/* Header: Title and Pricing */}
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-1 min-w-0">
            <h3 className="text-lg font-bold tracking-tight text-white flex items-center gap-2">
              <Terminal className="w-5 h-5 text-sky-400" />
              {name}
            </h3>
            <p className="text-xs text-neutral-400 leading-snug">
              {description}
            </p>
          </div>
          <div className="text-right shrink-0">
            <div className="text-xl font-mono font-bold text-white">${priceNumber}</div>
            <div className="text-[9px] uppercase tracking-widest text-neutral-500 font-black">{priceUnit}</div>
          </div>
        </div>

        {/* Intelligence Vectors (Feature Grid) */}
        <div className="border-y border-white/5 py-3">
          <div className="text-[9px] uppercase tracking-[0.2em] text-neutral-600 font-bold mb-2">Intelligence Vectors</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1.5">
            {[
              { icon: MessageSquare, label: "Hacker Forum Monitoring" },
              { icon: Globe, label: "Geographical Attribution" },
              { icon: Building, label: "Industry-Specific Targeting" },
              { icon: Zap, label: "Attack Vector Identification" },
              { icon: ShieldAlert, label: "Threat Actor Attribution" },
              { icon: ShieldAlert, label: "Ransomware Event Monitoring" },
              { icon: Database, label: "Exfiltrated Data Auctions" },
              { icon: Key, label: "Initial Access Brokerage" },
              { icon: Terminal, label: "Zero-Day & Exploit Intel" },
              { icon: Newspaper, label: "Mainstream OSINT Signals" }
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-2">
                <item.icon className="w-3.5 h-3.5 text-sky-500/70 shrink-0" />
                <span className="text-[11px] font-medium text-neutral-300 leading-tight truncate">{item.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Quota & Meta */}
        <div className="flex items-center justify-between gap-3">
          {quota && (
            <div className="px-2 py-0.5 rounded bg-sky-500/5 border border-sky-500/20 text-[10px] font-mono text-sky-400">
              {quota}
            </div>
          )}
          {note && (
            <div className="text-[10px] text-neutral-500 italic text-right">
              {note}
            </div>
          )}
        </div>
      </div>

      {/* Action Footer */}
      <div className="mt-5">
        <button
          onClick={onContact}
          className="w-full flex items-center justify-center gap-2 rounded-md border border-white/10 bg-white/5 px-4 py-2.5 text-xs font-bold text-neutral-300 transition-all hover:bg-white/10 hover:text-white"
        >
          Contact Us
        </button>
        {contactEmail && (
          <div className="flex items-center justify-center gap-1.5 mt-2">
            <Mail className="w-3 h-3 text-neutral-700" />
            <a href={`mailto:${contactEmail}`} className="text-[10px] text-neutral-600 hover:text-sky-400 font-mono transition-colors">
              {contactEmail}
            </a>
          </div>
        )}
      </div>
    </div>
  );
}

function AddonCardInvestigations({
  onBuy,
  onContact,
  checkoutUrl = true,
  contactEmail = "info@whiteintel.io", 
}) {
  return (
    <div className={`rounded-md ${frosted} p-5 flex flex-col justify-between transition-all hover:border-sky-500/40 group bg-[#000414] relative overflow-hidden`}>
      <div className="absolute -right-16 -top-16 w-64 h-64 bg-sky-500/5 rounded-full blur-3xl group-hover:bg-sky-500/10 transition-colors" />

      <div className="space-y-4 relative z-10">
        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-1 min-w-0">
            <h3 className="text-lg font-bold tracking-tight text-white flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-sky-400" />
              Investigation+
              <span className="text-[9px] px-1.5 py-0.5 border border-sky-500/30 text-sky-500/80 rounded uppercase tracking-[0.2em] font-medium leading-3">
                Beta
              </span>
            </h3>
            <p className="text-xs text-neutral-400 leading-snug">
              A dual-purpose neural engine for unmasking threat actor identities and conducting deep-trace forensic analysis on compromised victims.
            </p>
          </div>
          <div className="text-right shrink-0">
            <div className="text-xl font-mono font-bold text-white">$1,500</div>
            <div className="text-[9px] uppercase tracking-widest text-neutral-500 font-black">USD / Year</div>
          </div>
        </div>

        {/* Forensic Capabilities Grid */}
        <div className="border-y border-white/5 py-3">
          <div className="text-[9px] uppercase tracking-[0.2em] text-neutral-600 font-bold mb-2">Forensic Capabilities</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1.5">
            {[
              { icon: UserSearch, label: "Adversary Unmasking" },
              { icon: Target, label: "Detailed Victimology" },
              { icon: ShieldAlert, label: "Automated Risk Scoring" },
              { icon: Zap, label: "Target Prioritization" },
              { icon: Cpu, label: "Neural Identity Mapping" },
              { icon: Network, label: "Lateral Vector Analysis" },
              { icon: Fingerprint, label: "Forensic Footprinting" },
              { icon: Share2, label: "Pivot-Point Discovery" }
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-2">
                <item.icon className="w-3.5 h-3.5 text-sky-500/70 shrink-0" />
                <span className="text-[11px] font-medium text-neutral-300 leading-tight truncate">{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="mt-5 relative z-10">
        <button
          onClick={onContact}
          className="w-full flex items-center justify-center gap-2 rounded-md border border-white/10 bg-white/5 px-4 py-2.5 text-xs font-bold text-neutral-300 transition-all hover:bg-white/10 hover:text-white"
        >
          Contact Us
        </button>
        {contactEmail && (
          <div className="flex items-center justify-center gap-1.5 mt-2">
            <Mail className="w-3 h-3 text-neutral-700" />
            <a href={`mailto:${contactEmail}`} className="text-[10px] text-neutral-600 hover:text-sky-400 font-mono transition-colors">
              {contactEmail}
            </a>
          </div>
        )}
      </div>
    </div>
  );
}

function AddonCardTakedowns({ onContact, contactEmail = "info@whiteintel.io" }) {
  return (
    <div className={`rounded-md ${frosted} p-5 flex flex-col justify-between transition-all hover:border-sky-500/40 group bg-[#000414] relative overflow-hidden`}>
      <div className="absolute -right-16 -top-16 w-64 h-64 bg-violet-500/5 rounded-full blur-3xl group-hover:bg-violet-500/10 transition-colors" />

      <div className="space-y-4 relative z-10">
        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-1 min-w-0">
            <h3 className="text-lg font-bold tracking-tight text-white flex items-center gap-2">
              <Gavel className="w-5 h-5 text-sky-400" />
              Managed Takedowns
            </h3>
            <p className="text-xs text-neutral-400 leading-snug">
              Hand off the registrar abuse process to our analysts. We file,
              follow up, and document evidence on look-alike and phishing
              domains until they go down.
            </p>
          </div>
          <div className="text-right shrink-0">
            <div className="text-xl font-mono font-bold text-white">$1,000</div>
            <div className="text-[9px] uppercase tracking-widest text-neutral-500 font-black">
              USD / 10 Takedowns
            </div>
            <div className="text-[9px] text-neutral-500 font-mono mt-0.5">
              $100 each — minimum pack
            </div>
          </div>
        </div>

        {/* Eligibility (single-line) */}
        <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-amber-500/5 border border-amber-500/20 border-dashed">
          <ShieldAlert className="w-3.5 h-3.5 text-amber-400 shrink-0" />
          <p className="text-[11px] text-amber-100/90 leading-tight">
            Enterprise &amp; Threat Intelligence licenses only.
          </p>
        </div>

        {/* Capabilities Grid */}
        <div className="border-y border-white/5 py-3">
          <div className="text-[9px] uppercase tracking-[0.2em] text-neutral-600 font-bold mb-2">
            What's Included
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1.5">
            {[
              { icon: Target, label: "Look-alike Domains" },
              { icon: ShieldAlert, label: "Phishing &amp; Harvesters" },
              { icon: Send, label: "Registrar Abuse Filing" },
              { icon: ClipboardList, label: "Evidence Bundles" },
              { icon: Scale, label: "Provider Escalations" },
              { icon: ShieldCheck, label: "Lifecycle Tracking" },
              { icon: Sparkles, label: "Ad-hoc Takedowns" },
              { icon: Mail, label: "Analyst Communication" },
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-2">
                <item.icon className="w-3.5 h-3.5 text-sky-500/70 shrink-0" />
                <span className="text-[11px] font-medium text-neutral-300 leading-tight truncate">{item.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Quota chip */}
        <div className="flex items-center gap-2">
          <div className="px-2 py-0.5 rounded bg-sky-500/5 border border-sky-500/20 text-[10px] font-mono text-sky-400">
            10 takedowns / pack
          </div>
          <div className="text-[10px] text-neutral-500 italic truncate">
            Never expires while subscribed
          </div>
        </div>

        {/* Fairness note */}
        <div className="flex items-start gap-2 px-3 py-2 rounded-md bg-emerald-500/5 border border-emerald-500/20">
          <ShieldCheck className="w-3.5 h-3.5 text-emerald-400 shrink-0 mt-0.5" />
          <p className="text-[11px] text-emerald-100/85 leading-snug">
            <span className="font-semibold">Credit refund policy:</span> if a
            takedown is rejected by a non-ICANN-compliant or hostile registrar
            outside our reach, we restore the credit so you only pay for
            actionable cases.
          </p>
        </div>
      </div>

      {/* Footer */}
      <div className="mt-5 relative z-10">
        <button
          onClick={onContact}
          className="w-full flex items-center justify-center gap-2 rounded-md border border-white/10 bg-white/5 px-4 py-2.5 text-xs font-bold text-neutral-300 transition-all hover:bg-white/10 hover:text-white"
        >
          Contact Us
        </button>
        {contactEmail && (
          <div className="flex items-center justify-center gap-1.5 mt-2">
            <Mail className="w-3 h-3 text-neutral-700" />
            <a
              href={`mailto:${contactEmail}`}
              className="text-[10px] text-neutral-600 hover:text-sky-400 font-mono transition-colors"
            >
              {contactEmail}
            </a>
          </div>
        )}
      </div>
    </div>
  );
}

function ContactLicenseModal({ open, onClose, planName }) {
  const [name, setName] = useState("");
  const [msg, setMsg] = useState("");
  const [loading, setLoading] = useState(false);
  const [ok, setOk] = useState(null);
  const [err, setErr] = useState(null);
  const dialogRef = useRef(null);

  const [subscriptionType, setSubscriptionType] = useState("");

  const isThreatFeedAddon =
    typeof planName === "string" &&
    planName.toLowerCase().includes("threat feed api");

  const isInvestigations =
    typeof planName === "string" &&
    planName.toLowerCase().includes("investigation");

  const isManagedTakedowns =
    typeof planName === "string" &&
    planName.toLowerCase().includes("takedown");

  const isAddon = isThreatFeedAddon || isInvestigations || isManagedTakedowns;

  useEffect(() => {
    if (!open) return;
    const prev = document.activeElement;
    dialogRef.current?.focus();
    return () => prev && prev.focus && prev.focus();
  }, [open]);

  useEffect(() => {
    if (!open) {
      setName("");
      setMsg("");
      setLoading(false);
      setOk(null);
      setErr(null);
      setSubscriptionType("");
    }
  }, [open]);

  if (!open) return null;

  function getJwtToken() {
    try {
      return (
        localStorage.getItem("jwtToken") ||
        localStorage.getItem("token") ||
        sessionStorage.getItem("jwtToken") ||
        window.WHITEINTEL_TOKEN ||
        ""
      );
    } catch {
      return "";
    }
  }

  async function handleSend() {
    setOk(null);
    setErr(null);
    if (!msg.trim()) {
      setErr("Please enter a message.");
      return;
    }

    const category = isAddon ? "addon_inquiry" : "subscription_upgrade";
    const title = isAddon
      ? `${planName || "General"} Inquiry`
      : `${planName || "General"} License Inquiry (${
          subscriptionType === "yearly" ? "Yearly" : "Monthly"
        })`;

    const payload = {
      category,
      title,
      message: name.trim() ? `From: ${name}\n\n${msg}` : msg,
    };

    try {
      const res = await fetch(CONTACT_API, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getJwtToken()}`,
        },
        body: JSON.stringify(payload),
      });

      if ([401, 419, 440].includes(res.status)) {
        redirectToLogin();
        throw new Error(`AUTH_REDIRECT_${res.status}`);
      }
      if (!res.ok) {
        let errMsg = `HTTP ${res.status}`;
        try {
          const errJson = await res.json();
          if (errJson?.error) errMsg = errJson.error;
        } catch {}
        throw new Error(errMsg);
      }

      const json = await res.json();
      if (json && (json.success === true || json.status === "ok")) {
        setOk("Thanks! Your message has been sent. We’ll get back to you shortly.");
      } else {
        setOk(
          "You will receive an email from us shortly. If it does not appear in your inbox, please check your spam or junk folder."
        );
      }
      setName("");
      setMsg("");
    } catch (e) {
      setErr(e.message || "Failed to send your message. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[420]" role="dialog" aria-modal="true">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />

      {/* Panel */}
      <div
        ref={dialogRef}
        tabIndex={-1}
        className="absolute right-0 top-0 h-full w-full max-w-2xl bg-[#000414] border-l border-neutral-800 shadow-[0_0_20px_rgba(56,189,248,0.08)] outline-none flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-800">
          <div>
            <div className="text-sm text-neutral-400">{planName ? `Contact — ${planName}` : "Contact"}</div>
            <div className="text-xl font-semibold">
              Contact Us{planName ? ` for ${planName}` : ""}
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded hover:bg-white/5" title="Close">
            <X className="h-5 w-5 text-neutral-300" />
          </button>
        </div>

        {/* Banners */}
        {err && (
          <div className="px-5 pt-4">
            <div className="rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-200 flex items-start justify-between">
              <span className="pr-4">{err}</span>
              <button onClick={() => setErr(null)} className="ml-3 text-xs text-neutral-400 hover:text-white" type="button">
                Dismiss
              </button>
            </div>
          </div>
        )}
        {ok && (
          <div className="px-5 pt-4">
            <div className="rounded-md border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-200 flex items-start justify-between">
              <span className="pr-4">{ok}</span>
              <button onClick={() => setOk(null)} className="ml-3 text-xs text-neutral-400 hover:text-white" type="button">
                Dismiss
              </button>
            </div>
          </div>
        )}

        {/* Body */}
        <div className="p-5 space-y-6 overflow-y-auto flex-1">
          <div className={`rounded-md ${frosted} p-4 space-y-4`}>
            <div className="grid grid-cols-1 gap-4">
              {/* Name */}
              <div>
                <div className="text-[11px] uppercase tracking-wide text-neutral-400 mb-2">Your Name</div>
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Jane Doe"
                  className="w-full h-11 rounded-md border border-sky-900/30 bg-[#0a1224]/80 px-3 text-sm text-white outline-none focus-visible:ring-2 focus-visible:ring-sky-500/40"
                />
              </div>

              {/* Subscription Type — HIDDEN for Addons */}
              {!isAddon && (
                <div>
                  <div className="text-[11px] uppercase tracking-wide text-neutral-400 mb-2">Subscription Type</div>
                  <select
                    value={subscriptionType}
                    onChange={(e) => setSubscriptionType(e.target.value)}
                    className="w-full h-11 rounded-md border border-sky-900/30 bg-[#0a1224]/80 px-3 text-sm text-white outline-none focus-visible:ring-2 focus-visible:ring-sky-500/40"
                  >
                    <option value="">Select subscription type</option>
                    <option value="monthly">Monthly Subscription</option>
                    <option value="yearly">Yearly Subscription</option>
                  </select>
                </div>
              )}

              {/* Message */}
              <div>
                <div className="text-[11px] uppercase tracking-wide text-neutral-400 mb-2">Message</div>
                <textarea
                  value={msg}
                  onChange={(e) => setMsg(e.target.value)}
                  placeholder={
                    isThreatFeedAddon
                      ? "Tell us about your needs for the Threat Feed API…"
                      : `Tell us about your needs for ${planName || "the selected option"}…`
                  }
                  rows={6}
                  className="w-full rounded-md border border-sky-900/30 bg-[#0a1224]/80 p-3 text-sm text-white outline-none focus-visible:ring-2 focus-visible:ring-sky-500/40"
                />
              </div>
            </div>

            {/* KYC Banner — HIDDEN for Addons */}
            {!isAddon && (
              <div className="rounded-md border border-amber-500/30 bg-amber-500/10 p-4 text-amber-100">
                <div className="flex items-start gap-3">
                  <ShieldAlert className="h-5 w-5 mt-0.5 shrink-0" />
                  <div>
                    <div className="font-semibold">KYC & Due Diligence</div>
                    <p className="mt-1 text-sm opacity-90">
                      Because our threat intelligence may include sensitive or confidential information, we conduct Know Your
                      Customer (KYC) checks before enabling full access. To accelerate verification, please include as much context
                      as possible:
                    </p>
                    <ul className="mt-2 text-sm list-disc pl-5 space-y-1">
                      <li>Intended use cases</li>
                      <li>LinkedIn profile or team page</li>
                      <li>Company website and corporate email domain</li>
                      <li>Organization name, role, and business justification for access</li>
                      <li>Bug bounty platform profiles for researchers (HackerOne, Bugcrowd, etc.)</li>
                      <li>Personal website / portfolio</li>
                    </ul>
                    <p className="mt-2 text-xs opacity-80">
                      Submissions are reviewed by our compliance team. Incomplete requests may delay activation.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="p-5 flex items-center justify-end gap-3 border-t border-neutral-800">
          <button
            onClick={onClose}
            className="h-10 px-4 rounded-md border border-neutral-700 text-neutral-200 hover:bg-white/5"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            onClick={handleSend}
            disabled={loading}
            className="inline-flex items-center gap-2 h-10 px-4 rounded-md border border-sky-600/40 bg-sky-500/10 text-sky-200 hover:bg-sky-500/20 disabled:opacity-50"
          >
            <Send className="h-4 w-4" />
            {loading ? "Sending…" : "Send"}
          </button>
        </div>
      </div>
    </div>
  );
}